import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { zh } from '../i18n';
import { cn } from '../lib/cn';
import { IconX } from './icons';

/** Small shared primitives. Every touch target is >= 44 px (SPEC §10 / V6). */

type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger' | 'plain';

export function Button({
  variant = 'plain',
  small,
  block,
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  small?: boolean;
  block?: boolean;
}): JSX.Element {
  return (
    <button
      type={type}
      className={cn(
        'btn',
        variant !== 'plain' && `btn--${variant}`,
        small && 'btn--sm',
        block && 'btn--block',
        className,
      )}
      {...rest}
    />
  );
}

export function Card({
  glass,
  flush,
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
  glass?: boolean;
  flush?: boolean;
}): JSX.Element {
  return (
    <div
      className={cn('card', glass && 'card--glass', flush && 'card--flush', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Badge({
  tone = 'default',
  children,
}: {
  tone?: 'default' | 'primary' | 'success' | 'warn' | 'danger' | 'muted';
  children: ReactNode;
}): JSX.Element {
  return <span className={cn('badge', tone !== 'default' && `badge--${tone}`)}>{children}</span>;
}

export function Spinner({ label }: { label?: string }): JSX.Element {
  return (
    <span className="row">
      <span className="spinner" />
      <span className="small muted">{label ?? zh.app.loading}</span>
    </span>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {body ? <p className="small">{body}</p> : null}
      {action}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: (id: string) => ReactNode;
}): JSX.Element {
  const id = useId();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {hint ? <span className="field__hint">{hint}</span> : null}
      {error ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input {...props} className={cn('input', props.className)} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea {...props} className={cn('textarea', props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return <select {...props} className={cn('select', props.className)} />;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: readonly { readonly value: T; readonly label: string }[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    <div className="segmented" role="tablist" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className="segmented__item"
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Bottom sheet — used for confirmations that need naming a consequence. */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="sheet"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet__panel" ref={panelRef} tabIndex={-1}>
        <div className="row row--between" style={{ marginBottom: '0.5rem' }}>
          <h2>{title}</h2>
          <Button variant="ghost" small aria-label={zh.app.close} onClick={onClose}>
            <IconX size={18} />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ErrorText({ error }: { error: unknown }): JSX.Element | null {
  if (!error) return null;
  const message = error instanceof Error ? error.message : zh.app.unknownError;
  return (
    <p className="field__error" role="alert">
      {message}
    </p>
  );
}
